import React from "react";

import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import CappedPage from "@/src/components/common/CappedPage";
import NavTabs from "@/src/components/common/NavTabs";
import PageTitle from "@/src/components/common/PageTitle";
import GreographicGroupsConfiguration from "@/src/components/platform/configuration/GreographicGroupsConfiguration";
import useAbility from "@/src/hooks/useAbility";
import useUrlTab from "@/src/hooks/useUrlTab";

import EmbedProfilesConfiguration from "./embedProfiles/EmbedProfilesConfiguration";
import StorageAccessConfiguration from "./storageAccess/StorageAccessConfiguration";

import type { JSX } from "react";

import type { NavTabItem } from "@/src/components/common/NavTabs";
import type { AppState } from "@/src/reducers";

const TAB_GEOGRAPHIC_GROUPS = "geographic-groups";
const TAB_STORAGE_ACCESS = "storage-access";
const TAB_EMBED_PROFILES = "embed-profiles";

function OrganizationConfiguration(): JSX.Element {
  const { t } = useTranslation("editor-organization");

  const { canAccess } = useAbility();
  const orgaOptions = useSelector(
    (state: AppState) =>
      state.fetchReducer.orga_config.data?.organization_options,
  );
  const { storageAccess, embedProfiles } = canAccess(orgaOptions);

  const tabList: NavTabItem[] = [
    {
      value: TAB_GEOGRAPHIC_GROUPS,
      label: t("tabs.geographicGroups"),
      content: <GreographicGroupsConfiguration />,
    },
    ...(storageAccess
      ? [
          {
            value: TAB_STORAGE_ACCESS,
            label: t("tabs.storageAccess"),
            content: <StorageAccessConfiguration />,
          },
        ]
      : []),
    ...(embedProfiles
      ? [
          {
            value: TAB_EMBED_PROFILES,
            label: t("tabs.embedProfiles"),
            content: <EmbedProfilesConfiguration />,
          },
        ]
      : []),
  ];
  const availableTabs = tabList.map((tab) => tab.value);
  const { activeTab, setActiveTab } = useUrlTab({
    availableTabs,
    defaultTab: TAB_GEOGRAPHIC_GROUPS,
  });

  return (
    <CappedPage contentClassName="py-15">
      <PageTitle className="mb-6">{t("configTitle")}</PageTitle>
      <NavTabs
        onValueChange={setActiveTab}
        tabList={tabList}
        value={activeTab}
      />
    </CappedPage>
  );
}

export default OrganizationConfiguration;
